export interface FileValidationOptions {
  maxSize?: number;
  allowedFormats?: string[];
  allowedExtensions?: string[];
}

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

export type TFunction = (
  key: string,
  replacements?: Record<string, string | number>
) => string;


export const DEFAULT_FILE_VALIDATION: FileValidationOptions = {
  maxSize: 5 * 1024 * 1024,
  allowedFormats: [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg",
    "image/gif",
    "image/webp",
  ],
  allowedExtensions: ["jpg", "jpeg", "png", "gif", "webp", "svg"],
};

export const PROFILE_IMAGE_VALIDATION: FileValidationOptions = {
  maxSize: 2 * 1024 * 1024,
  allowedFormats: [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg",
    "image/webp",
  ],
  allowedExtensions: ["jpg", "jpeg", "png", "webp", "svg"],
};

export const DOCUMENT_VALIDATION: FileValidationOptions = {
  maxSize: 10 * 1024 * 1024,
  allowedFormats: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  allowedExtensions: ["pdf", "doc", "docx"],
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (
    Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i]
  );
};

export const getFileExtension = (fileName: string): string =>
  fileName ? fileName.split(".").pop()?.toLowerCase() || "" : "";

export const validateFileSize = (
  file: File,
  maxSize: number,
  t: TFunction
): FileValidationResult => {
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: t("fileTooLarge", { size: maxSizeMB }),
    };
  }
  return { valid: true };
};

export const validateFileFormat = (
  file: File,
  allowedFormats: string[],
  t: TFunction
): FileValidationResult => {
  if (
    allowedFormats.length > 0 &&
    !allowedFormats.includes(file.type)
  ) {
    const formats = allowedFormats
      .map((f) => f.split("/")[1].toUpperCase())
      .join(", ");
    return {
      valid: false,
      error: t("invalidFileFormat", { formats }),
    };
  }
  return { valid: true };
};

export const validateFileExtension = (
  file: File,
  allowedExtensions: string[],
  t: TFunction
): FileValidationResult => {
  const ext = getFileExtension(file.name);

  if (
    allowedExtensions.length > 0 &&
    ext &&
    !allowedExtensions.includes(ext)
  ) {
    const formats = allowedExtensions
      .map((e) => e.toUpperCase())
      .join(", ");
    return {
      valid: false,
      error: t("invalidFileExtension", { formats }),
    };
  }
  return { valid: true };
};

export const validateFile = (
  file: File,
  options: FileValidationOptions,
  t: TFunction
): FileValidationResult => {
  const {
    maxSize = DEFAULT_FILE_VALIDATION.maxSize!,
    allowedFormats = DEFAULT_FILE_VALIDATION.allowedFormats!,
    allowedExtensions = DEFAULT_FILE_VALIDATION.allowedExtensions!,
  } = options;

  const sizeValidation = validateFileSize(file, maxSize, t);
  if (!sizeValidation.valid) return sizeValidation;

  // const formatValidation = validateFileFormat(file, allowedFormats, t);
  // if (!formatValidation.valid) return formatValidation;

  // const extensionValidation = validateFileExtension(file, allowedExtensions, t);
  // if (!extensionValidation.valid) return extensionValidation;

  return { valid: true };
};

export const isImageFile = (file: File): boolean =>
  file.type.startsWith("image/");

export const isDocumentFile = (file: File): boolean =>
  [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ].includes(file.type);

export const createImagePreview = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!isImageFile(file)) {
      reject(new Error("File is not an image"));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Error reading file"));
    reader.readAsDataURL(file);
  });

export const getFileIconType = (fileName: string): string => {
  const ext = getFileExtension(fileName);

  const iconMap: Record<string, string> = {
    jpg: "image",
    jpeg: "image",
    png: "image",
    gif: "image",
    webp: "image",
    svg: "image",
    pdf: "pdf",
    doc: "word",
    docx: "word",
    xls: "excel",
    xlsx: "excel",
    ppt: "powerpoint",
    pptx: "powerpoint",
    zip: "archive",
    rar: "archive",
    "7z": "archive",
    js: "code",
    ts: "code",
    jsx: "code",
    tsx: "code",
    html: "code",
    css: "code",
    json: "code",
    txt: "text",
    md: "text",
  };

  return iconMap[ext] || "file";
};
